import React from 'react';
import ConfirmDialog from '../ConfirmDialog';
import { useDateFormat } from '../../context/LocaleContext';
import { CONFLICT_WORDING, conflictLine } from '../../domain/calendarWording';
import s from './ConflictDialog.module.css';

// "You are about to double-book yourself."
//
// ── MMS's dialog, verbatim ──────────────────────────────────────────────────
//   Scheduling Conflicts Detected
//
//   There are conflicts with this scheduling change. Would you like to ignore
//   the conflicts and continue, or cancel to undo the change?
//
//   Scheduling Conflicts: 1
//     • <Teacher Name> is already attending another event at this time
//       on 9/5/2026
//
//   [Cancel]  [Ignore & Continue]
//
// * It warns; it does not block. "Ignore & Continue" is the primary action and
//   the save goes through, as with the cancellation dialog's unreachable-
//   recipient warning: a teacher deliberately overlaps a make-up with a group
//   class often enough that blocking would make the feature something to
//   switch off.
//
// * Cancel undoes the change, in MMS's framing: useEventForm asks before it
//   writes anything, so cancelling leaves the event as it was and the drawer
//   open with the edits in it.
//
// * The count is stated apart from the list: "Scheduling Conflicts: 1" is a
//   heading above the bullets. Two weekly series collide every week for a year,
//   so the number carries the scale while the first few bullets carry the
//   specifics (CONFLICT_SAMPLE in domain/conflicts.js).
//
// The wording is per person, not per event: MMS names the attendee who is in
// two places, and names the teacher on a non-teaching event with no attendees.
// So the teacher attends everything, and any student on both events is
// double-booked too — see `doubleBookedBy` in domain/conflicts.js.
//
// MMS also puts a copy icon beside the count; this dialog has none.

// Every string this dialog says is `CONFLICT_WORDING` in
// domain/calendarWording.js, shared with the phones' dialogs:
//
// * `line` — MMS shows one name. Where two events share several students they
//   are all double-booked and all listed; the joining comma is ours, the
//   sentence around it MMS's.
// * `lineForYou` — the same sentence for a teacher this app has no name for
//   (#385), the default state of a new studio. MMS's sentence with its subject
//   in the first person, so the two lines still read as one dialog.
// * `horizon` — ours: the count is a count within the horizon, and on a series
//   that repeats indefinitely saying so is the difference between a number and
//   a claim.
export const WORDING = CONFLICT_WORDING;

// The one line about one conflict: who is in two places, students first,
// because "you are busy" is the fact the teacher already knows. It falls
// through to the teacher, who attends everything on their own calendar.
//
// The sentence is chosen rather than the subject (#385): a function returning
// a name would have to put some word in the subject slot when there is none,
// so the no-name case picks `lineForYou` instead. The rule is `conflictLine` in
// domain/calendarWording.js.
export const lineFor = conflictLine;

export default function ConflictDialog({
  // Empty, not `'You'`: `lineFor` answers an absent name with a sentence that
  // does not need one (#385).
  open, result, teacherName = '', studentName = null, onCancel, onConfirm,
}) {
  // An ISO date-only string through a local-time Date renders the previous day
  // west of Greenwich, so the core's substitution is used.
  const shortDate = useDateFormat();
  if (!open || !result) return null;

  return (
    <ConfirmDialog
      open={open}
      title={WORDING.title}
      message={(
        <span>
          <span className={s.body}>{WORDING.body}</span>

          <span className={s.count}>{WORDING.count(result.count)}</span>

          {result.conflicts.map((c, i) => (
            <span key={`${c.with.seriesId}-${c.with.ruleDate}-${i}`} className={s.item}>
              {lineFor(
                c,
                { teacherName, studentName },
                shortDate(c.date) || c.date,
              )}
            </span>
          ))}

          {result.beyondHorizon ? (
            <span className={s.horizon}>
              {WORDING.horizon(shortDate(result.horizon.end) || result.horizon.end)}
            </span>
          ) : null}
        </span>
      )}
      confirmLabel={WORDING.confirm}
      cancelLabel=""
      discardLabel="Cancel"
      onDiscard={onCancel}
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  );
}
